import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  ApolloProvider as Provider,
  from,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { PropsWithChildren, useEffect, useLayoutEffect, useMemo } from "react";
import { useToken } from "./token";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { signOut as signOutAuth } from "aws-amplify/auth";
import { useTenantIdPathParam } from "hooks/useTenant";

const removeTypenameLink = removeTypenameFromVariables();

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/graphql`
    : "http://localhost:4000/graphql",
});

const createErrorLink = () =>
  onError(({ graphQLErrors, networkError }) => {
    // We don't want to redirect to the login page if we are already on the login page
    // that could cause an infinite redirect
    // since / hits the API
    const isOnLoginPage = window.location.pathname === "/";

    const handleAuthError = () => {
      if (!isOnLoginPage) {
        // Mark that we're logging out to prevent redirect loops
        localStorage.setItem("logout", "true");

        // Sign out from AWS Amplify to clear all authentication state
        signOutAuth()
          .then(() => {
            // Force a full page reload to clear any cached state
            window.location.href = "/";
          })
          .catch((signOutError) => {
            console.error("Error during signout:", signOutError);
            // Even if signout fails, still redirect to clear the app state
            window.location.href = "/";
          });
      }
    };

    if (graphQLErrors) {
      graphQLErrors.forEach(({ extensions }) => {
        if (extensions?.code === "UNAUTHENTICATED") {
          handleAuthError();
        }
      });
    }

    if (
      networkError &&
      "statusCode" in networkError &&
      networkError.statusCode === 401
    ) {
      handleAuthError();
    }
  });

export const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      HdHandler: {
        keyFields: ["handlerId", "activityLocation", "sourceType", "seqNumber"],
      },
      Payment: {
        fields: {
          facilities: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      TierIIReport: {
        fields: {
          assignees: {
            merge(_, incoming) {
              return incoming;
            },
          },
          reviewers: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      Submission: {
        fields: {
          facilitySubmissions: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  connectToDevTools: import.meta.env.VITE_ENV !== "release",
});

export function ApolloProvider(props: PropsWithChildren<object>) {
  const { getIdTokenRefreshIfExpired, isAuthenticated } = useToken();

  const currentTenantId = useTenantIdPathParam();

  const tenantLink = useMemo(
    () =>
      setContext((_, { headers }) => {
        return {
          headers: {
            ...headers,
            "x-encamp-tenant-id": currentTenantId ?? "",
          },
        };
      }),
    [currentTenantId]
  );

  // useLayoutEffect (as opposed to useEffect) forces this to be synchronous so it will finish
  // running before the component is rendered. That is necessary because we need to make the
  // client.setLink call before any child components attempt to actually use the client
  useLayoutEffect(() => {
    const authLink = setContext(async (_, { headers }) => {
      const idToken = await getIdTokenRefreshIfExpired();
      return {
        headers: {
          ...headers,
          "Content-Type": "application/json",
          authorization: idToken ? `Bearer ${idToken}` : "",
        },
      };
    });

    client.setLink(
      from([
        removeTypenameLink,
        createErrorLink(),
        authLink,
        tenantLink,
        httpLink,
      ])
    );
  }, [getIdTokenRefreshIfExpired, tenantLink]);

  useEffect(() => {
    if (!isAuthenticated) {
      client.clearStore();
    }
  }, [isAuthenticated]);

  return <Provider client={client}>{props.children}</Provider>;
}
